<template>
  <div class="p-learning-detail-cont__wrapper -narrow-top">
    <div class="p-learning-detail-cont">
      <!-- 注釈は左上、コピーボタンは右上。画面が狭いときは注釈側だけが折り返し、ボタンは右上に残る -->
      <div class="c-table-explain">
        <p class="p-learning-detail-cont__note">
          ※獲得☆数：学習を1回終えるたびに正答率に応じてもらえる☆（1〜3個）の合計です。<br>
          ※学習回数：「学習を開始」から最後の問題まで終えると、1回とカウントします。
        </p>
        <copy-table-button :target-table="learningStatusTableRef" />
      </div>
      <div class="learningDetailCont__main">
        <div class="p-learning-detail-figure">
          <div class="sortTable">
            <div class="sortTable__inner">
              <vue-good-table
                :columns="columns"
                :rows="rows"
                styleClass="sortTable__table"
                ref="refVgtComponent"
              >
                <template #table-column="props">
                  <span
                    class="sortTable__header--vgt"
                    v-if="props.column.label_html"
                    v-html="props.column.label_html"
                  >
                  </span>
                  <span class="sortTable__header--vgt" v-else
                    >{{ props.column.label }}
                  </span>
                </template>

                <template #table-row="props">
                  <span
                    :class="{
                      'sortTable__span--belowAverage': checkBelowAverage(
                        props.column.field,
                        props.row[props.column.field]
                      )
                    }"
                  >
                    <template v-if="props.column.field == 'name'">
                      <a :href="linkToStudentDetail(props.row)">
                        <span class="label">{{props.row.name}}</span>
                      </a>
                    </template>
                    <template v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </template>
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {ref, onMounted, computed, watch} from "vue";
  import { VueGoodTable } from "vue-good-table-next";
  import { format, parseISO } from "date-fns";
  import 'vue-good-table-next/dist/vue-good-table-next.css';

  // FIXME: store/modules/teacher/learning_status_class で定義指定いる型定義を設定しておきたい
  const props = defineProps<{
    classProgressData: any;
    period: number;
  }>()

  // 表に表示するデータ
  const rows = ref([]);

  const refVgtComponent = ref(null)

  // vue-good-tableコンポーネントの子要素から取得した、実体の<table>要素
  // （コピー対象は、テーブルコピー共通処理に渡すため実DOM要素である必要がある）
  //
  // $refsはリアクティブなオブジェクトではないため、computedにして
  // refVgtComponent.value.$refs.tableを直接参照する形にすると、
  // 「refVgtComponent.valueが最初にセットされたタイミング」でのみ再評価され、
  // その時点で$refs.tableがまだ存在しない場合はnullのまま更新されなくなってしまう。
  // そのため、$refs.tableの存在を確認できてから
  // 代入する方式にしている（下のonMounted内のポーリング処理を参照）。
  const learningStatusTableRef = ref<HTMLTableElement | null>(null)

  const columns = computed(() => {
    let previousStudyTimeHtml = "学習時間[分]<br />（前回）"
    let latestStudyTimeHtml = "学習時間[分]<br />（今回）"

    // 期間が「月間」、「年間」の場合、学習時間の単位は「時間」
    if(props.period == 2 || props.period == 3){
      previousStudyTimeHtml = "学習時間[時間]<br />（前回）"
      latestStudyTimeHtml = "学習時間[時間]<br />（今回）"
    }

    // 表のカラム定義
    return [
      {
        label: "出席番号",
        label_html: "出席番号",
        field: "number"
      },
      {
        label: "名前",
        field: "name"
      },
      {
        label: "進捗率",
        field: "progressRate",
        formatFn: displayPercentage
      },
      {
        label: "初回正答率",
        label_html: "初回<br />正答率",
        field: "correctAnswerRateInitial",
        formatFn: displayPercentage
      },
      {
        label: "最新正答率",
        label_html: "最新<br />正答率",
        field: "correctAnswerRateLatest",
        formatFn: displayPercentage
      },
      {
        label: "最新と初回の差",
        label_html: "最新と<br />初回の差",
        field: "correctAnswerRateDifference",
        formatFn: displayPercentage
      },
      {
        label: "獲得☆数",
        label_html: "獲得<br />☆数",
        field: "numberOfStars"
      },
      {
        label: "学習回数",
        label_html: "学習<br />回数",
        field: "studyCount"
      },
      {
        label: "学習した問題数",
        label_html: "学習した<br />問題数",
        field: "studiedQuestions"
      },
      {
        label: "学習時間（前回）",
        label_html: previousStudyTimeHtml,
        field: "studiedHoursPrevious",
        formatFn: displayFraction
      },
      {
        label: "学習時間（今回）",
        label_html: latestStudyTimeHtml,
        field: "studiedHoursLatest",
        formatFn: displayFraction
      },
      {
        label: "学習日数",
        label_html: "学習<br />日数",
        field: "studiedDays"
      },
      {
        label: "最終学習日",
        label_html: "最終<br />学習日",
        field: "latestStudyDate",
        formatFn: displayDate
      }
    ];
  })

  const averageProgressData = computed(() => {
    // 元データを壊さないようにコピーを作成
    let averageData = JSON.parse(JSON.stringify(props.classProgressData.averageData));
    if(averageData){
      // 期間が「月間」、「年間」の場合、学習時間の単位を「時間」に変更
      if(props.period == 2 || props.period == 3){
        averageData["studiedHoursPrevious"] = Math.floor(averageData["studiedHoursPrevious"] / 60 * 10) / 10;
        averageData["studiedHoursLatest"] = Math.floor(averageData["studiedHoursLatest"] / 60 * 10) / 10;
      }
    }

    return averageData
  })

  const studyProgressData = computed(() => {
    // 元データを壊さないようにコピーを作成
    let studyData = JSON.parse(JSON.stringify(props.classProgressData.datasets));
    if(studyData){
      // 期間が「月間」、「年間」の場合、学習時間の単位を「時間」に変更
      if(props.period == 2 || props.period == 3){
        for(let studiedDataset of studyData){
          studiedDataset["studiedHoursPrevious"] = Math.floor(studiedDataset["studiedHoursPrevious"] / 60 * 10) / 10;
          studiedDataset["studiedHoursLatest"] = Math.floor(studiedDataset["studiedHoursLatest"] / 60 * 10) / 10;
        }
      }
    }

    return studyData
  })

  function checkBelowAverage(fieldName: string, value: any) {
    // 平均値より低いかどうかチェックする。
    // 文字列の場合は、falseを返す
    if (typeof value === "string") {
      return false;
    }
    return value < averageProgressData.value[fieldName];
  }

  function displayPercentage(value: number) {
    return value.toFixed(1) + "%";
  }

  function displayFraction(value: number) {
    return value.toFixed(1);
  }

  function displayDate(dateString: string) {
    // ISO-8601 の日付を "12月03日" の形に変換する
    const fmtString = "M月dd日";
    try {
      let date = parseISO(dateString);
      return format(date, fmtString);
    } catch {
      return "";
    }
  }

  function formattedAverageData() {
    // フッター行に表示する順番にソートされたフォーマット済み平均値データを返す
    return [
      averageProgressData.value.progressRate.toFixed(1) + "%",
      averageProgressData.value.correctAnswerRateInitial.toFixed(1) + "%",
      averageProgressData.value.correctAnswerRateLatest.toFixed(1) + "%",
      averageProgressData.value.correctAnswerRateDifference.toFixed(1) + "%",
      averageProgressData.value.numberOfStars,
      averageProgressData.value.studyCount,
      averageProgressData.value.studiedQuestions,
      averageProgressData.value.studiedHoursPrevious.toFixed(1),
      averageProgressData.value.studiedHoursLatest.toFixed(1),
      averageProgressData.value.studiedDays,
      averageProgressData.value.latestStudyDate,
    ]
  }

  /* テーブルデータ更新 */
  function updateLearningStatusTableData() {
    rows.value = studyProgressData.value.map(el => el);
  }

  /* テーブルフッター(平均値)データ更新 */
  function updateLearningStatusTableFooter() {
    // このタイミングでは、onMounted内のポーリングで既にtFootが作成済みのはずだが、
    // 型上はnullの可能性があるためガードしておく
    if (!learningStatusTableRef.value) return
    // vue-good-tableコンポーネントの子要素からtableフッターを取得する
    const footer = learningStatusTableRef.value.tFoot
    // 平均値セル群取得
    let cells = footer.rows[0].getElementsByTagName("td");

    // 平均値として表示したい順番でデータを取得する
    const averageData = formattedAverageData()

    // フッターの平均データを更新
    for (let i = 0; i < averageData.length; i++) {
      let cell = cells[i];
      cell.textContent = averageData[i]
    }
  }

  function getQueryString(queryParams) {
    // 渡されたクエリパラメータ用データをクエリ文字列化する
    return (Object.entries(queryParams)
      .map(queryParam => `${queryParam[0]}=${queryParam[1]}`)
    )
  }

  function linkToStudentDetail(rowData) {
    let url = new URL(location.toString())
    // T8-3へのリンクを生成する
    let student_pathname = url.pathname.replace("/class/", "/student/")
    url.pathname = student_pathname
    return url + "&" + getQueryString({student_id: rowData.id})
  }

  onMounted(() => {
    // フッターは、DOM生成後に追加したいためmounted()で追加する
    updateLearningStatusTableData()
    // マウント時、対象のコンポーネントがまだ存在しない可能性があるため、描写されるまで待つ。
    const interval = setInterval(() => {
      // <vue-good-table ref="vgtcomponent> でマウントしたコンポーネントを狙う
      // XXX: 例外的。あまり推奨されていない。
      // cf. http://reiyoshizawa.com/how-to-use-refs-in-vue-js/
      // cf. https://vuejs.org/v2/guide/components-edge-cases.html
      if ((refVgtComponent.value as VueGoodTable).$refs.table) {
        // コピー機能に渡すため、実体の<table>要素を保持しておく
        learningStatusTableRef.value = (refVgtComponent.value as VueGoodTable).$refs.table
        // <tfoot> 作成処理
        // vue-good-tableコンポーネントの子要素からtableを取り出し、フッターを追加する
        const footer = learningStatusTableRef.value.createTFoot()
        // <tr> 作成
        const row = footer.insertRow()

        // <th> 作成
        let th = document.createElement("th")
        th.innerHTML = averageProgressData.value.name
        th.setAttribute("colspan", "2")
        row.appendChild(th)

        // 平均値として表示したい順番でデータを取得する
        const averageData = formattedAverageData()

        // フッターに平均データのセル(td)作成
        for (let i = 0; i < averageData.length; i++) {
          let cell = row.insertCell();
          cell.textContent = averageData[i]
        }
        clearInterval(interval)
      }
    }, 50)
  })

  watch(
    () => props.classProgressData,
    () => {
      // テーブルデータ・平均値フッターのアップデート
      updateLearningStatusTableData()
      updateLearningStatusTableFooter()
    }
  )
</script>


<style lang="scss" scoped>
.sortTable__header--vgt {
  display: block;
  text-align: center;
}

.sortTable__span--belowAverage {
  color: #e25612;
}
</style>
